import { useState } from "react";
import { tiposIndividuo, estadosFitossanitarios, classesRisco, recomendacoesManejo, statusRevisaoList } from "@/mocks/cta";

interface MapFilterPanelProps {
  collapsed: boolean;
  onToggle: () => void;
  onApplyFilters: (filters: Record<string, string>) => void;
  onClearFilters: () => void;
  onSaveView: () => void;
  onExportSelection: () => void;
  onGenerateReport: () => void;
}

export default function MapFilterPanel({
  collapsed,
  onToggle,
  onApplyFilters,
  onClearFilters,
  onSaveView,
  onExportSelection,
  onGenerateReport,
}: MapFilterPanelProps) {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    projeto: true,
    localizacao: false,
    individuo: false,
    fitossanidade: true,
    manejo: false,
    revisao: false,
    outros: false,
  });

  const toggleSection = (key: string) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const updateFilter = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApply = () => {
    onApplyFilters(filters);
  };

  const handleClear = () => {
    setFilters({});
    onClearFilters();
  };

  const projetos = [
    "Inventário Arbóreo — Setor Bela Vista",
    "Revitalização Verde — Praças Bela Vista",
    "Monitoramento de Árvores — Bela Vista",
  ];

  const bairros = ["Setor Bela Vista", "Setor Pedro Ludovico", "Jardim América", "Setor Sul", "Setor Central"];

  const especiesUnicas = [
    "Handroanthus chrysotrichus",
    "Caesalpinia pluviosa",
    "Licania tomentosa",
    "Mangifera indica",
    "Roystonea oleracea",
    "Delonix regia",
    "Jacaranda mimosifolia",
    "Ficus benjamina",
  ];

  const tecnicos = ["Carlos Mendes", "Ana Oliveira", "Rafael Torres", "Juliana Costa", "Pedro Alves", "Fernanda Lima", "Marcos Vinicius", "Gabriel Santos"];

  const selectClass = "w-full text-xs border border-background-200 rounded-md px-2.5 py-2 bg-white text-foreground-700 focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-300 transition-colors";

  if (collapsed) {
    return (
      <div className="w-10 bg-white border-r border-background-200/70 flex flex-col items-center py-3 gap-3 shrink-0">
        <button onClick={onToggle} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors" title="Expandir filtros">
          <i className="ri-arrow-right-s-line text-foreground-500 text-lg"></i>
        </button>
        <button onClick={handleApply} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-primary-50 text-primary-500 transition-colors" title="Aplicar filtros">
          <i className="ri-filter-3-line text-base"></i>
        </button>
        <button onClick={handleClear} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-500 transition-colors" title="Limpar filtros">
          <i className="ri-close-line text-base"></i>
        </button>
      </div>
    );
  }

  const SectionHeader = ({ keyName, label, icon }: { keyName: string; label: string; icon: string }) => (
    <button
      onClick={() => toggleSection(keyName)}
      className="w-full flex items-center justify-between gap-2 py-2 text-xs font-semibold text-foreground-700 font-heading hover:text-foreground-900 transition-colors"
    >
      <span className="flex items-center gap-1.5 whitespace-nowrap">
        <i className={`${icon} text-xs`} aria-hidden="true"></i>
        {label}
      </span>
      <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${expandedSections[keyName] ? "rotate-180" : ""}`}></i>
    </button>
  );

  return (
    <aside className="w-[260px] bg-white border-r border-background-200/70 flex flex-col shrink-0 h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-3 border-b border-background-200/70">
        <div className="flex items-center gap-2">
          <i className="ri-filter-3-line text-accent-500 text-base" aria-hidden="true"></i>
          <span className="text-sm font-semibold text-foreground-900 font-heading whitespace-nowrap">Filtros</span>
        </div>
        <button onClick={onToggle} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors" title="Recolher filtros">
          <i className="ri-arrow-left-s-line text-foreground-500 text-base"></i>
        </button>
      </div>

      {/* Filter sections */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
        {/* Projeto */}
        <div>
          <SectionHeader keyName="projeto" label="Projeto" icon="ri-folder-chart-line" />
          {expandedSections.projeto && (
            <select
              value={filters.projeto || ""}
              onChange={(e) => updateFilter("projeto", e.target.value)}
              className={selectClass}
            >
              <option value="">Todos os projetos</option>
              {projetos.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          )}
        </div>

        {/* Localização */}
        <div>
          <SectionHeader keyName="localizacao" label="Localização" icon="ri-map-pin-line" />
          {expandedSections.localizacao && (
            <div className="space-y-2 pt-1">
              <select value={filters.bairro || ""} onChange={(e) => updateFilter("bairro", e.target.value)} className={selectClass}>
                <option value="">Todos os bairros</option>
                {bairros.map((b) => (<option key={b} value={b}>{b}</option>))}
              </select>
              <input
                type="text"
                placeholder="Logradouro..."
                value={filters.logradouro || ""}
                onChange={(e) => updateFilter("logradouro", e.target.value)}
                className={selectClass}
              />
            </div>
          )}
        </div>

        {/* Tipo de Indivíduo */}
        <div>
          <SectionHeader keyName="individuo" label="Tipo de Indivíduo" icon="ri-plant-line" />
          {expandedSections.individuo && (
            <select value={filters.tipo || ""} onChange={(e) => updateFilter("tipo", e.target.value)} className={selectClass}>
              <option value="">Todos os tipos</option>
              {tiposIndividuo.map((t) => (<option key={t} value={t}>{t}</option>))}
            </select>
          )}
        </div>

        {/* Espécie */}
        <div>
          <SectionHeader keyName="especie" label="Espécie" icon="ri-leaf-line" />
          {expandedSections.especie && (
            <select value={filters.especie || ""} onChange={(e) => updateFilter("especie", e.target.value)} className={selectClass}>
              <option value="">Todas as espécies</option>
              {especiesUnicas.map((e) => (<option key={e} value={e}>{e}</option>))}
            </select>
          )}
        </div>

        {/* Fitossanidade */}
        <div>
          <SectionHeader keyName="fitossanidade" label="Fitossanidade" icon="ri-heart-pulse-line" />
          {expandedSections.fitossanidade && (
            <div className="space-y-2 pt-1">
              <select value={filters.estadoFito || ""} onChange={(e) => updateFilter("estadoFito", e.target.value)} className={selectClass}>
                <option value="">Todos os estados</option>
                {estadosFitossanitarios.map((e) => (<option key={e} value={e}>{e.charAt(0).toUpperCase() + e.slice(1)}</option>))}
              </select>
              <select value={filters.classeRisco || ""} onChange={(e) => updateFilter("classeRisco", e.target.value)} className={selectClass}>
                <option value="">Todas as classes de risco</option>
                {classesRisco.map((r) => (<option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>))}
              </select>
            </div>
          )}
        </div>

        {/* Manejo */}
        <div>
          <SectionHeader keyName="manejo" label="Recomendação de Manejo" icon="ri-scissors-line" />
          {expandedSections.manejo && (
            <select value={filters.recomendacao || ""} onChange={(e) => updateFilter("recomendacao", e.target.value)} className={selectClass}>
              <option value="">Todas as recomendações</option>
              {recomendacoesManejo.map((r) => (<option key={r} value={r}>{r}</option>))}
            </select>
          )}
        </div>

        {/* Status de Revisão */}
        <div>
          <SectionHeader keyName="revisao" label="Status de Revisão" icon="ri-check-double-line" />
          {expandedSections.revisao && (
            <select value={filters.statusRevisao || ""} onChange={(e) => updateFilter("statusRevisao", e.target.value)} className={selectClass}>
              <option value="">Todos os status</option>
              {statusRevisaoList.map((s) => (<option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>))}
            </select>
          )}
        </div>

        {/* Outros */}
        <div>
          <SectionHeader keyName="outros" label="Outros" icon="ri-more-line" />
          {expandedSections.outros && (
            <div className="space-y-2 pt-1">
              <select value={filters.tecnico || ""} onChange={(e) => updateFilter("tecnico", e.target.value)} className={selectClass}>
                <option value="">Todos os técnicos</option>
                {tecnicos.map((t) => (<option key={t} value={t}>{t}</option>))}
              </select>
              <select value={filters.areaVerde || ""} onChange={(e) => updateFilter("areaVerde", e.target.value)} className={selectClass}>
                <option value="">Todas as áreas/UCs</option>
                <option value="Praça da Bela Vista">Praça da Bela Vista</option>
                <option value="Área Verde Córrego Cascavel">Área Verde Córrego Cascavel</option>
                <option value="Parque Municipal Bela Vista">Parque Municipal Bela Vista</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="border-t border-background-200/70 p-3 space-y-2">
        <button onClick={handleApply} className="w-full btn-primary text-xs py-2 whitespace-nowrap">
          <i className="ri-filter-3-line text-sm" aria-hidden="true"></i>
          Aplicar filtros
        </button>
        <button onClick={handleClear} className="w-full btn-outline text-xs py-2 whitespace-nowrap">
          <i className="ri-close-line text-sm" aria-hidden="true"></i>
          Limpar filtros
        </button>
        <div className="flex gap-1.5">
          <button onClick={onSaveView} className="flex-1 btn-outline text-xs py-1.5 whitespace-nowrap" title="Salvar visão">
            <i className="ri-bookmark-line text-xs" aria-hidden="true"></i>
            <span className="text-[10px]">Salvar</span>
          </button>
          <button onClick={onExportSelection} className="flex-1 btn-outline text-xs py-1.5 whitespace-nowrap" title="Exportar seleção">
            <i className="ri-download-line text-xs" aria-hidden="true"></i>
            <span className="text-[10px]">Exportar</span>
          </button>
          <button onClick={onGenerateReport} className="flex-1 btn-outline text-xs py-1.5 whitespace-nowrap" title="Gerar relatório">
            <i className="ri-file-chart-line text-xs" aria-hidden="true"></i>
            <span className="text-[10px]">Relatório</span>
          </button>
        </div>
      </div>
    </aside>
  );
}